import {
  Box,
  Paper,
  Typography,
  Divider,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Chip,
  createFilterOptions,
  List,
  ListItem
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';
import { useInvoiceController } from 'api/controllers/InvoiceController';

import {
  CurrencyDropDown,
  DocumentType,
  Invoice,
  InvoiceExpenseType,
  InvoiceSection,
  InvoiceStages,
  PaymentChannel,
  PaymentStatus,
  InvoicePatch,
  Counterparty
} from 'openapi';

import { ApprovalButtons } from 'components/forms/InvoiceVerificationForms/ApprovalButtons';
import { GatesCard } from 'components/forms/InvoiceVerificationForms/GatesCard';
import { LabelValueField } from 'components/shared/LabelField/LabelValueField';
import { Modal } from 'components/shared/Modal/Modal';
import { showSuccessToastMessage } from 'components/shared/Toast/Toast';

import { usePermissions } from 'context/PermissionsContext';
import { useTranslations } from 'context/TranslationContext';

import { useModal } from 'hooks/useModal';

import { DocumentConfig } from 'utils/config/documentEntries';
import { IBAN_STATUS_COLORS, IbanStatus } from 'utils/constants/ibans';
import {
  DOCUMENT_TYPES_TRANSLATION_KEYS,
  PAYMENT_CHANNELS_TRANSLATION_KEYS,
  PAYMENT_STATUS_STYLES,
  PAYMENT_STATUSES_TRANSLATION_KEYS
} from 'utils/constants/invoices';
import { getExpenseTypeDialogState } from 'utils/helpers/expenseTypeHelpers';
import {
  getInvoiceDataDefaultValues,
  getInvoiceDisplayIbans,
  getSummaryCounterpartyDisplay,
  getSummaryDataFormValues
} from 'utils/helpers/invoiceHelpers';
import {
  InvoiceDetails,
  InvoiceSectionKey
} from 'utils/interfaces/InvoiceProps';

import { verificationFormItem } from 'styles/pages/InvoiceVerificationStyle';

import { EditInvoiceDataForm } from './EditInvoiceDataForm';
import { AdditionalInvoiceFields } from './ReceiverForm';

interface ApprovalsProps {
  currentInvoice: Invoice;
  handleApprove: () => void;
  handleSettle: () => void;
  handleBack?: () => void;
  handleReject: (reason: string) => void;
  handleReturnToValidated: () => void;
  handleBackFromMiddleStep: () => void;
  handleSaveData: (
    values: InvoicePatch & AdditionalInvoiceFields,
    section: InvoiceSectionKey
  ) => Promise<void>;
  documentConfig: DocumentConfig;
}

export const Approvals = ({
  currentInvoice,
  handleBack,
  handleApprove,
  handleSettle,
  handleReject,
  handleReturnToValidated,
  handleBackFromMiddleStep,
  handleSaveData,
  documentConfig
}: ApprovalsProps) => {
  const { translate } = useTranslations();
  const { getExpenseTypes, getAllTagsByCompanyId, getCurrencies } =
    useAdditionalFieldsController();
  const { updateExpenseTypeDuringApprovalById } = useInvoiceController();
  const { getCounterpartyById } = useCounterPartyController();
  const { permissions } = usePermissions();

  const { control, formState, getValues, watch, reset, setValue, trigger } =
    useForm<InvoiceDetails>({
      mode: 'onChange',
      defaultValues: getInvoiceDataDefaultValues(currentInvoice)
    });

  const [selectedExpenseTypeId, setSelectedExpenseTypeId] = useState<
    number | null
  >(currentInvoice.expenseType?.id || null);
  const [expenseTypeOptions, setExpenseTypeOptions] = useState<
    InvoiceExpenseType[]
  >([]);
  const [tags, setTags] = useState<string[]>(currentInvoice.tags || []);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyDropDown[]>();
  const [invoiceCounterparty, setInvoiceCounterparty] =
    useState<Counterparty>();

  const filter = createFilterOptions<string>();

  const {
    isOpen: isEditExpenseTypeModalOpen,
    closeModal: closeEditExpenseTypeModal,
    openModal: openEditExpenseTypeModal
  } = useModal();
  const {
    isOpen: isEditTagsModalOpen,
    closeModal: closeEditTagsModal,
    openModal: openEditTagsModal
  } = useModal();
  const {
    isOpen: isEditSummaryModalOpen,
    closeModal: closeEditSummaryModal,
    openModal: openEditSummaryModal
  } = useModal();

  const isReimbursement = Boolean(currentInvoice.isReimbursement);
  const { counterpartyValue, showCounterparty, showReimburseTo } =
    getSummaryCounterpartyDisplay(currentInvoice);

  const displayIbans = useMemo(
    () => getInvoiceDisplayIbans(currentInvoice),
    [currentInvoice]
  );

  const hasPaymentSelectedIbansInInvoice = useMemo(
    () =>
      currentInvoice.ibans?.filter((iban) => iban.isSelectedForPayment).length,
    [currentInvoice.ibans]
  );

  const hasPaymentSelectedIbansInCounterparty = useMemo(
    () =>
      invoiceCounterparty?.ibans.filter((iban) => iban.isSelectedForPayment)
        .length,
    [invoiceCounterparty?.ibans]
  );

  const selectedForPaymentIbanInCounterparty = useMemo(
    () =>
      invoiceCounterparty?.ibans.find((iban) => iban.isSelectedForPayment)
        ?.iban,
    [invoiceCounterparty?.ibans]
  );

  const hasSelectedIbanForPayment = isReimbursement
    ? Boolean(currentInvoice.reimbursementIban)
    : Boolean(hasPaymentSelectedIbansInInvoice) ||
      Boolean(hasPaymentSelectedIbansInCounterparty);

  const fetchExpenseTypes = useCallback(async () => {
    const expenseTypeOptionValues = await getExpenseTypes(
      Number(currentInvoice.companyId)
    );
    setExpenseTypeOptions(expenseTypeOptionValues);
  }, [getExpenseTypes, currentInvoice]);

  const fetchAvailableTags = useCallback(async () => {
    const tagsResponse = await getAllTagsByCompanyId(
      Number(currentInvoice.companyId)
    );
    setAvailableTags(Array.from(new Set([...tags, ...tagsResponse])));
  }, [getAllTagsByCompanyId, currentInvoice.companyId, tags]);

  const fetchCurrencies = useCallback(async () => {
    const currenciesResponse = await getCurrencies();
    setCurrencies(currenciesResponse);
  }, [getCurrencies, setCurrencies]);

  const fetchCounterparty = useCallback(async () => {
    const companyCounterpartyId = currentInvoice.isReimbursement
      ? currentInvoice.reimbursementCompanyCounterpartyId
      : currentInvoice.companyCounterpartyId;

    if (!currentInvoice.companyId || !companyCounterpartyId) {
      setInvoiceCounterparty(undefined);
      return;
    }

    const response = await getCounterpartyById(
      Number(currentInvoice.companyId),
      Number(companyCounterpartyId)
    );
    setInvoiceCounterparty(response);
  }, [getCounterpartyById, currentInvoice]);

  const handleSaveExpenseType = useCallback(async () => {
    if (
      !selectedExpenseTypeId ||
      !currentInvoice.companyId ||
      !currentInvoice.id
    ) {
      return;
    }

    await updateExpenseTypeDuringApprovalById(
      currentInvoice.companyId,
      currentInvoice.id,
      selectedExpenseTypeId
    );
    showSuccessToastMessage(
      translate('messages.expenseTypeSuccessfullyUpdated')
    );
    closeEditExpenseTypeModal();
  }, [
    selectedExpenseTypeId,
    updateExpenseTypeDuringApprovalById,
    closeEditExpenseTypeModal,
    translate
  ]);

  const handleSaveTags = useCallback(async () => {
    closeEditTagsModal();
    await handleSaveData(
      {
        tags
      },
      InvoiceSection.SUMMARY
    );
  }, [tags, handleSaveData, currentInvoice, closeEditTagsModal]);

  const handleSaveSummaryData = useCallback(async () => {
    closeEditSummaryModal();
    await handleSaveData(
      getSummaryDataFormValues(
        currentInvoice,
        expenseTypeOptions || [],
        getValues
      ),
      InvoiceSection.SUMMARY
    );
  }, [handleSaveData, currentInvoice, getValues, closeEditSummaryModal]);

  const handleBackForNoDocument = () => {
    if (!handleBack) {
      return;
    }

    handleBackFromMiddleStep();
    handleBack();
  };

  const handleCloseEditSummaryModal = () => {
    closeEditSummaryModal();
    reset();
  };

  const handleCloseEditTagsModal = () => {
    closeEditTagsModal();
    setTags(currentInvoice.tags || []);
  };

  const handleCloseEditExpenseTypeModal = () => {
    setSelectedExpenseTypeId(currentInvoice.expenseType?.id || null);
    closeEditExpenseTypeModal();
  };

  useEffect(() => {
    if (expenseTypeOptions.length) {
      return;
    }

    fetchExpenseTypes();
  }, [fetchExpenseTypes, fetchAvailableTags, fetchCurrencies]);

  useEffect(() => {
    fetchCounterparty();
  }, [fetchCounterparty, currentInvoice]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  useEffect(() => {
    fetchAvailableTags();
  }, [fetchAvailableTags]);

  useEffect(() => {
    setSelectedExpenseTypeId(currentInvoice.expenseType?.id || null);
  }, [currentInvoice]);

  useEffect(() => {
    setAvailableTags((previousTags) =>
      Array.from(new Set([...tags, ...previousTags]))
    );
  }, [tags]);

  useEffect(() => {
    setTags(currentInvoice.tags || []);
  }, [currentInvoice]);

  const {
    icon: IconComponent,
    backgroundColor,
    textColor
  } = PAYMENT_STATUS_STYLES[currentInvoice.paymentStatus as PaymentStatus];
  const label = translate(
    PAYMENT_STATUSES_TRANSLATION_KEYS[
      currentInvoice.paymentStatus as PaymentStatus
    ]
  );
  const paymentChannel =
    PAYMENT_CHANNELS_TRANSLATION_KEYS[
      currentInvoice.paymentChannel as PaymentChannel
    ] &&
    translate(
      PAYMENT_CHANNELS_TRANSLATION_KEYS[
        currentInvoice.paymentChannel as PaymentChannel
      ]
    );

  const { isExpenseTypeSelectDisabled, isSaveExpenseTypeDisabled } =
    getExpenseTypeDialogState(
      expenseTypeOptions,
      selectedExpenseTypeId,
      currentInvoice.expenseType?.id
    );

  return (
    <Box sx={{ width: '100%' }}>
      <ApprovalButtons
        handleReject={handleReject}
        handleBack={
          currentInvoice.documentType === DocumentType.NO_DOCUMENT
            ? handleBackForNoDocument
            : handleBack
        }
        handleApprove={handleApprove}
        handleReturnToValidated={handleReturnToValidated}
        handleSettle={handleSettle}
        currentInvoice={currentInvoice}
        documentConfig={documentConfig}
      />
      <Paper
        elevation={3}
        sx={{
          padding: 1,
          backgroundColor: '#fefefe',
          marginTop: 2,
          border: '1px solid #e5e7eb',
          boxShadow: 'none'
        }}
      >
        {(currentInvoice.stage === InvoiceStages.APPROVED ||
          currentInvoice.stage === InvoiceStages.FINALIZED ||
          currentInvoice.stage === InvoiceStages.SETTLED) &&
          currentInvoice.canEditSummary && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                sx={{
                  fontSize: '0.7rem',
                  padding: '0rem 0.5rem'
                }}
                variant="outlined"
                size="small"
                onClick={openEditSummaryModal}
              >
                {translate('buttons.edit')}
              </Button>
            </Box>
          )}
        {/* Receiver Information */}
        <LabelValueField
          label={translate('labels.receiver')}
          value={currentInvoice.shortNameCompany || '-'}
        />

        {showCounterparty && (
          <LabelValueField
            label={translate('labels.counterparty')}
            value={counterpartyValue}
          />
        )}

        {showReimburseTo && (
          <LabelValueField
            label={translate('labels.reimburseTo')}
            value={currentInvoice.reimbursementCounterpartyName || '-'}
          />
        )}

        <LabelValueField
          label={translate('labels.documentType')}
          value={
            currentInvoice.documentType
              ? translate(
                  DOCUMENT_TYPES_TRANSLATION_KEYS[currentInvoice.documentType]
                )
              : '-'
          }
        >
          {documentConfig.showPaymentStatus && (
            <Tooltip title={paymentChannel}>
              <Chip
                label={label}
                icon={
                  IconComponent ? <IconComponent fontSize="small" /> : undefined
                }
                sx={{
                  backgroundColor,
                  color: textColor,
                  '& .MuiChip-icon': {
                    color: textColor
                  }
                }}
              />
            </Tooltip>
          )}
        </LabelValueField>

        <Divider sx={{ my: 2 }} />
        {/* Document and Payment Status */}

        <LabelValueField
          label={translate('labels.invoiceNumberAndDate')}
          value={`${currentInvoice.invoiceNumber || '-'} / ${
            currentInvoice.invoiceDate
          }`}
        />
        {documentConfig.allowExpenseType && (
          <LabelValueField
            label={translate('labels.expenseType')}
            value={
              currentInvoice.expenseType?.isActive
                ? currentInvoice.expenseType?.name || '-'
                : `${currentInvoice.expenseType?.name} ${translate(
                    'labels.deactivatedExpenseTypeValue'
                  )}`
            }
          >
            {currentInvoice.stage === InvoiceStages.VALIDATED &&
              permissions.INVOICES.update && (
                <Button
                  sx={{
                    fontSize: '0.7rem',
                    padding: '0rem 0.5rem'
                  }}
                  variant="outlined"
                  size="small"
                  onClick={openEditExpenseTypeModal}
                >
                  {translate('buttons.change')}
                </Button>
              )}
          </LabelValueField>
        )}

        <LabelValueField
          label={translate('labels.totalAmount')}
          value={`${currentInvoice.invoiceAmount?.toFixed(2)} ${
            currentInvoice.currency
          } (${translate('labels.vatBase')}: ${
            currentInvoice.vatBase?.toFixed(2) || 0
          } ${currentInvoice.currency})`}
        />

        <Divider sx={{ my: 2 }} />

        <LabelValueField
          label={translate('labels.comment')}
          value={currentInvoice.documentDescription || '-'}
        />

        <LabelValueField
          label={translate('labels.tags')}
          value={currentInvoice.tags?.join(', ') || '-'}
        >
          {currentInvoice.stage === InvoiceStages.VALIDATED &&
            permissions.INVOICES.update && (
              <Button
                sx={{
                  fontSize: '0.7rem',
                  padding: '0rem 0.5rem'
                }}
                variant="outlined"
                size="small"
                onClick={openEditTagsModal}
              >
                {translate('buttons.change')}
              </Button>
            )}
        </LabelValueField>
      </Paper>
      <Paper
        elevation={3}
        sx={{
          padding: 1,
          backgroundColor: '#fefefe',
          marginTop: 2,
          border: '1px solid #e5e7eb',
          boxShadow: 'none'
        }}
      >
        <>
          {!isReimbursement &&
            !hasPaymentSelectedIbansInInvoice &&
            selectedForPaymentIbanInCounterparty && (
              <Typography fontSize="0.875rem" textAlign="left" paddingLeft={2}>
                <strong>{translate('labels.paymentSelectedIban')}:</strong>{' '}
                {selectedForPaymentIbanInCounterparty}
              </Typography>
            )}
          <List>
            {displayIbans.length ? (
              displayIbans.map(
                ({ iban, id, extractedInDocumentId, isSelectedForPayment }) => {
                  const isNew = extractedInDocumentId === currentInvoice.id;
                  const isSelected = isSelectedForPayment;

                  return (
                    <ListItem sx={{ paddingY: 0.5 }} key={id ?? iban}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize="0.875rem">{iban}</Typography>

                        {isSelected && (
                          <Tooltip
                            title={translate('messages.ibanSelectedForPayment')}
                          >
                            <Chip
                              size="small"
                              label={translate('labels.ibanSelectedForPayment')}
                              sx={IBAN_STATUS_COLORS[IbanStatus.SELECTED]}
                            />
                          </Tooltip>
                        )}

                        {isNew && (
                          <Tooltip
                            title={translate('messages.ibanNewlyExtracted')}
                          >
                            <Chip
                              size="small"
                              label={translate('labels.ibanNewlyExtracted')}
                              sx={IBAN_STATUS_COLORS[IbanStatus.NEW]}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                  );
                }
              )
            ) : (
              <Typography fontSize="0.875rem" textAlign="left" paddingLeft={2}>
                {translate(documentConfig.labels.noIbans)}
              </Typography>
            )}
          </List>
          {!hasSelectedIbanForPayment && (
            <Typography fontSize="0.875rem" textAlign="left" paddingLeft={2}>
              {translate('messages.noSelectedIbans')}
            </Typography>
          )}
        </>
      </Paper>

      {documentConfig.allowExpenseType && (
        <GatesCard currentInvoice={currentInvoice} translate={translate} />
      )}
      <Modal
        headerTitle={translate('labels.expenseTypeEdit')}
        isOpen={isEditExpenseTypeModalOpen}
        hide={handleCloseEditExpenseTypeModal}
        maxWidth="sm"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl sx={verificationFormItem}>
            <InputLabel>{`${translate('labels.expenseType')}*`}</InputLabel>
            <Select
              value={selectedExpenseTypeId || ''}
              sx={{ textAlign: 'left' }}
              label={`${translate('labels.expenseType')}*`}
              disabled={isExpenseTypeSelectDisabled}
              onChange={(e) => setSelectedExpenseTypeId(Number(e.target.value))}
              renderValue={(selected) => {
                const selectedOption = expenseTypeOptions.find(
                  (expenseType) => expenseType.id === selected
                ) as InvoiceExpenseType;

                return (
                  <span
                    style={{
                      color:
                        selectedOption && !selectedOption.isActive
                          ? 'gray'
                          : 'inherit'
                    }}
                  >
                    {!selectedOption.isActive
                      ? `${selectedOption.name} ${translate(
                          'labels.deactivatedExpenseTypeValue'
                        )}`
                      : selectedOption.name}
                  </span>
                );
              }}
            >
              {expenseTypeOptions &&
                expenseTypeOptions.map(
                  (expenseType) =>
                    expenseType.isActive && (
                      <MenuItem key={expenseType.id} value={expenseType.id}>
                        {expenseType.name}
                      </MenuItem>
                    )
                )}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSaveExpenseType}
              variant="contained"
              disabled={isSaveExpenseTypeDisabled}
            >
              {translate('buttons.save')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        headerTitle={translate('labels.editTags')}
        isOpen={isEditTagsModalOpen}
        hide={handleCloseEditTagsModal}
        maxWidth="sm"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl sx={verificationFormItem}>
            <Autocomplete
              fullWidth
              freeSolo
              multiple
              disableCloseOnSelect
              options={availableTags}
              value={tags}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== '') {
                  const isExisting = options.some(
                    (option) =>
                      params.inputValue.toLowerCase() === option.toLowerCase()
                  );
                  if (!isExisting) {
                    filtered.push(`Add "${params.inputValue}"`);
                  }
                }

                return filtered;
              }}
              getOptionLabel={(option) => {
                if (option.startsWith('Add "')) {
                  return option;
                }
                return option;
              }}
              onChange={(event, newValue) => {
                const lastValue = newValue[newValue.length - 1];
                if (lastValue && lastValue.startsWith('Add "')) {
                  const newTag = lastValue.slice(5, -1);
                  const updatedTags = [...tags, newTag];
                  setTags(updatedTags);
                } else {
                  const uniqueTags = Array.from(new Set(newValue));
                  setTags(uniqueTags);
                }
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.startsWith('Add "') ? <em>{option}</em> : option}
                </li>
              )}
              renderValue={(value: string[], getItemProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    sx={{ cursor: 'initial' }}
                    variant="outlined"
                    label={option}
                    {...getItemProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('labels.tags')}
                  placeholder={translate('labels.addATag')}
                />
              )}
            />
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleSaveTags} variant="contained">
              {translate('buttons.save')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        headerTitle={translate('labels.editDocument')}
        isOpen={isEditSummaryModalOpen}
        hide={handleCloseEditSummaryModal}
        maxWidth="lg"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EditInvoiceDataForm
            currentInvoice={currentInvoice}
            control={control}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            trigger={trigger}
            availableTags={availableTags}
            setAvailableTags={setAvailableTags}
            currencies={currencies || []}
            expenseTypeOptions={expenseTypeOptions || []}
            documentConfig={documentConfig}
            isInEditMode
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              disabled={!formState.isValid}
              onClick={handleSaveSummaryData}
              variant="contained"
            >
              {translate('buttons.save')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
